import { Stack } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { SchemaSection } from './SchemaSection'
import { SchemaGroup } from './SchemaGroup'
import { SectionCard } from '../layout/SectionCard'
import { SlideEditor } from '../slides/SlideEditor'

// Schema V2 collections remain noun-agnostic; Schema V1 slides keep their historical editor.
import { CollectionEditor } from '../collections/CollectionEditor'
import { matchesCondition } from '../../utils/conditions'
import { buildUiLayout, groupedSetupSchema } from '../../utils/uiGroups'

export function SchemaForm({
  schema,
  templateManifest,
  configuration,
  errors,
  updateValue,
  addAsset,
  addRenditionPreview,
  assetUrls,
  assetSourceUrls,
  selectedSlide,
  setSelectedSlide,
  addSlide,
  removeSlide,
  moveSlide,
  selectedItems,
  setSelectedItem,
  addCollectionItem,
  removeCollectionItem,
  moveCollectionItem,
  duplicateCollectionItem,
  leading = null,
  formatInPreview = false,
}) {
  const { t } = useTranslation()
  const slideCollection = (schema.collections || []).find((entry) => entry.path === 'slides')

  const shared = {
    configuration,
    templateManifest,
    errors,
    updateValue,
    addAsset,
    addRenditionPreview,
    assetUrls,
    assetSourceUrls,
  }

  const renderSection = (section) => {
    if (!matchesCondition(section.visibleWhen, configuration)) return null
    if (schema.dialect === 'studio-editor-v2/schema-v1' && section.collection === 'slides') {
      return (
        <SlideEditor
          key={section.id}
          section={section}
          collection={slideCollection}
          {...shared}
          selectedSlide={selectedSlide}
          setSelectedSlide={setSelectedSlide}
          addSlide={addSlide}
          removeSlide={removeSlide}
          moveSlide={moveSlide}
        />
      )
    }
    if (section.collection) {
      const collection = (schema.collections || []).find((entry) => entry.id === section.collection)
      if (!collection) return null
      if (section.ui?.control === 'slide-editor') {
        return (
          <SlideEditor
            key={section.id}
            section={section}
            collection={collection}
            {...shared}
            selectedSlide={selectedItems?.[collection.id] || 0}
            setSelectedSlide={(index) => setSelectedItem(collection.id, index)}
            addSlide={() => addCollectionItem(collection.id)}
            removeSlide={(index) => removeCollectionItem(collection.id, index)}
            moveSlide={(index, direction) => moveCollectionItem(collection.id, index, direction)}
          />
        )
      }
      return (
        <CollectionEditor
          key={section.id}
          section={section}
          collection={collection}
          {...shared}
          selected={(selectedItems && selectedItems[collection.id]) || 0}
          onSelect={(index) => setSelectedItem(collection.id, index)}
          onAdd={() => addCollectionItem(collection.id)}
          onRemove={(index) => removeCollectionItem(collection.id, index)}
          onMove={(index, direction) => moveCollectionItem(collection.id, index, direction)}
          onDuplicate={(index) => duplicateCollectionItem(collection.id, index)}
        />
      )
    }
    return <SchemaSection key={section.id} section={section} {...shared} />
  }

  // Templates that ship `schema.ui` drive the redesigned card layout. Everything else
  // keeps the historical one-card-per-section rendering, unchanged.
  const layout = buildUiLayout(groupedSetupSchema(schema, templateManifest, { withFormat: !formatInPreview }))
  if (!layout) {
    return (
      <Stack gap="md">
        {leading && <SectionCard title={t('editorV2.form.general')}>{leading}</SectionCard>}
        {(schema.sections || []).map(renderSection)}
      </Stack>
    )
  }

  // `leading` (the template selector) is the first field of the General card — or of the
  // first card with fields when a schema names its groups differently.
  const groupsWithFields = layout.groups.filter((group) => group.fields.some((field) =>
    matchesCondition(field.visibleWhen, configuration)
  ))
  const leadingGroup = groupsWithFields.find((group) => /^general$/i.test(group.id || ''))
    || groupsWithFields[0]
    || null

  return (
    <Stack gap="md">
      {leading && !leadingGroup && <SectionCard title={t('editorV2.form.general')}>{leading}</SectionCard>}
      {layout.groups.map((group) => {
        const visibleFields = group.fields.filter((field) =>
          matchesCondition(field.visibleWhen, configuration)
        )
        const sections = group.sections.map(renderSection).filter(Boolean)

        // A group holding only a collection (the Slides card) lets that editor draw its
        // own card rather than nesting one card inside another.
        if (visibleFields.length === 0) {
          return sections.length ? <div key={group.id}>{sections}</div> : null
        }

        return (
          <SchemaGroup
            key={group.id}
            group={{ ...group, fields: visibleFields }}
            leading={group === leadingGroup ? leading : null}
            {...shared}
          >
            {sections}
          </SchemaGroup>
        )
      })}
      {layout.leftovers.map(renderSection)}
    </Stack>
  )
}
